import { GIT_BRANCH } from "./generated/git_branch.js";

export enum RuntimeEnvironmentKey
{
  MAIN = "main",
  PROD = "prod",
}

const API_URLS: Record<RuntimeEnvironmentKey, string> = {
  [RuntimeEnvironmentKey.MAIN]: "https://dev-api.justsmash.work",
  [RuntimeEnvironmentKey.PROD]: "https://prod-api.justsmash.work",
};

const LOCAL_API_URL = "http://localhost:3001";

const WEBSOCKET_URLS: Record<RuntimeEnvironmentKey, string> = {
  [RuntimeEnvironmentKey.MAIN]: "wss://ws-brickbreaker-dev.blockroyale.biz/ws",
  [RuntimeEnvironmentKey.PROD]: "wss://ws-brickbreaker.blastix.net/ws",
};

const LEADERBOARD_URLS: Record<RuntimeEnvironmentKey, string> = {
  [RuntimeEnvironmentKey.MAIN]: "https://royalesmash-dev.galaxy4gm.com",
  [RuntimeEnvironmentKey.PROD]: "https://royalesmash.galaxy4gm.com",
};

const RUNTIME_ENV_OVERRIDE_KEY = "runtime_environment_override";
const SERVER_ROUTING_OVERRIDE_KEY = "server_routing_override";

// Where the home server told this client to connect instead of the addresses
// baked into the build. Written only by ServerRoutingService, honoured in every
// environment including PROD; the manual dev hacks below still win on DEV.
export interface ServerRoutingTarget
{
  api_url: string;
  ws_url: string | null;
  leaderboard_url: string | null;
}

const LOCAL_HOSTS = new Set( [ 'localhost', '127.0.0.1', '::1', '[::1]' ] );

function normalizeRoutingUrl( value: unknown, secure: 'https:' | 'wss:' ): string | null
{
  if ( typeof value !== 'string' ) return null;
  const trimmed = value.trim();
  if ( !trimmed ) return null;

  let parsed: URL;
  try { parsed = new URL( trimmed ); }
  catch { return null; }

  const plain = secure === 'https:' ? 'http:' : 'ws:';
  const allowed = parsed.protocol === secure || ( parsed.protocol === plain && LOCAL_HOSTS.has( parsed.hostname ) );
  if ( !allowed || parsed.username || parsed.password ) return null;

  return secure === 'https:'
    ? parsed.origin + parsed.pathname.replace( /\/+$/, '' )
    : parsed.toString();
}

export function normalizeServerRoutingTarget( raw: unknown ): ServerRoutingTarget | null
{
  if ( raw === null || typeof raw !== 'object' ) return null;
  const record = raw as Record<string, unknown>;
  const api_url = normalizeRoutingUrl( record.api_url, 'https:' );
  if ( !api_url ) return null;
  return {
    api_url,
    ws_url: normalizeRoutingUrl( record.ws_url, 'wss:' ),
    leaderboard_url: normalizeRoutingUrl( record.leaderboard_url, 'https:' ),
  };
}

export function isSameServerRoutingTarget( a: ServerRoutingTarget | null, b: ServerRoutingTarget | null ): boolean
{
  if ( a === null || b === null ) return a === b;
  return a.api_url === b.api_url && a.ws_url === b.ws_url && a.leaderboard_url === b.leaderboard_url;
}

let server_routing_override: ServerRoutingTarget | null | undefined;

const ROUTING_HOME_OVERRIDE_KEY = "routing_home_override";
let routing_home_param_applied = false;

function readRoutingHomeOverride(): string | null
{
  if ( !routing_home_param_applied )
  {
    routing_home_param_applied = true;
    try
    {
      const params = new URLSearchParams( window.location.search );
      if ( params.has( "routing_home" ) )
      {
        const value = params.get( "routing_home" )?.trim() ?? "";
        if ( value ) localStorage.setItem( ROUTING_HOME_OVERRIDE_KEY, value );
        else
        {
          localStorage.removeItem( ROUTING_HOME_OVERRIDE_KEY );
          localStorage.removeItem( SERVER_ROUTING_OVERRIDE_KEY );
          server_routing_override = undefined;
        }
      }
    }
    catch {}
  }
  const stored = safeLocalStorageGet( ROUTING_HOME_OVERRIDE_KEY )?.trim();
  return stored ? stored.replace( /\/+$/, "" ) : null;
}

export function getServerRoutingOverride(): ServerRoutingTarget | null
{
  if ( server_routing_override === undefined )
  {
    const raw = safeLocalStorageGet( SERVER_ROUTING_OVERRIDE_KEY );
    let parsed: unknown = null;
    if ( raw )
    {
      try { parsed = JSON.parse( raw ); }
      catch { parsed = null; }
    }
    server_routing_override = normalizeServerRoutingTarget( parsed );
  }
  return server_routing_override;
}

export function setServerRoutingOverride( target: ServerRoutingTarget | null ): void
{
  server_routing_override = target;
  try
  {
    if ( target ) localStorage.setItem( SERVER_ROUTING_OVERRIDE_KEY, JSON.stringify( target ) );
    else localStorage.removeItem( SERVER_ROUTING_OVERRIDE_KEY );
  }
  catch {}
}

function safeLocalStorageGet( key: string ): string | null
{
  try { return localStorage.getItem( key ); }
  catch { return null; }
}

function safeLocalStorageSet( key: string, value: string ): void
{
  try { localStorage.setItem( key, value ); }
  catch {}
}

function normalizeRuntimeEnvironmentKey( value: unknown ): RuntimeEnvironmentKey | null
{
  if ( value === RuntimeEnvironmentKey.MAIN || value === 'dev' ) return RuntimeEnvironmentKey.MAIN;
  if ( value === RuntimeEnvironmentKey.PROD || value === 'production' ) return RuntimeEnvironmentKey.PROD;
  return null;
}

export function getRuntimeEnvironmentOverride(): RuntimeEnvironmentKey | null
{
  return normalizeRuntimeEnvironmentKey( safeLocalStorageGet( RUNTIME_ENV_OVERRIDE_KEY ) );
}

export function setRuntimeEnvironmentOverride( key: RuntimeEnvironmentKey ): void
{
  safeLocalStorageSet( RUNTIME_ENV_OVERRIDE_KEY, key );
}

export function getActiveRuntimeEnvironmentKey(): RuntimeEnvironmentKey
{
  return normalizeRuntimeEnvironmentKey( getRuntimeEnvironmentOverride() ?? GIT_BRANCH ) ?? RuntimeEnvironmentKey.MAIN;
}

export function getRuntimeEnvironmentLabel(): 'DEV' | 'PROD'
{
  return GlobalSettings.ENV === RuntimeEnvironmentKey.PROD ? 'PROD' : 'DEV';
}

export function getNextRuntimeEnvironmentKey(): RuntimeEnvironmentKey
{
  return getActiveRuntimeEnvironmentKey() === RuntimeEnvironmentKey.PROD
    ? RuntimeEnvironmentKey.MAIN
    : RuntimeEnvironmentKey.PROD;
}

export class GlobalSettings
{
  public static readonly RENDERING_MAX_FPS = 60;
  public static readonly DEBUG = false;
  public static readonly ATHENA_ANALYTICS_PING_INTERVAL_MS = 30000;

  public static readonly OFFERS_ENABLED = true;
  // Ads are switched off: no banner, interstitial or rewarded video, whatever
  // the server's ads config says. The ad code stays in place.
  public static readonly ADS_ENABLED = false;
  // Promo offers are switched off: trigger pop-ups, the Continue special-offer
  // panel, placement (1+1) and chained offers. Shop packs stay.
  public static readonly PROMO_OFFERS_ENABLED = false;
  public static readonly APPSFLYER_ATTRIBUTION_ENABLED = false;

  // Shared studio-wide support/debug-auth service (same backend used by the
  // other BlockRoyale games) — gates the debug console behind a password in
  // prod builds.
  public static readonly SUPPORT_BASE_URL = "https://support.justsmash.work";

  public static get LEADERBOARD_BASE_URL(): string
  {
    return getServerRoutingOverride()?.leaderboard_url
      ?? LEADERBOARD_URLS[GlobalSettings.ENV]
      ?? LEADERBOARD_URLS[RuntimeEnvironmentKey.MAIN];
  }

  // The addresses baked into the build for the active environment, ignoring
  // any routing override: this is the only server allowed to redirect us.
  public static get HOME_API_BASE_URL(): string
  {
    // Dev-only: point the routing check at a local server, either via
    // localStorage.setItem('routing_home_override', 'http://localhost:3034')
    // or by opening the game once with ?routing_home=http://localhost:3034
    // (an empty ?routing_home= clears it and drops any stored redirect).
    if ( GlobalSettings.ENV !== RuntimeEnvironmentKey.PROD )
    {
      const override = readRoutingHomeOverride();
      if ( override ) return override;
    }
    return API_URLS[GlobalSettings.ENV] ?? API_URLS[RuntimeEnvironmentKey.MAIN];
  }

  public static SHOW_FPS_OVERLAY = false;
  public static DEBUG_EMPTY_LEVEL = false;
  public static DEBUG_DISABLE_VFX3D = false;
  public static DEBUG_NO_FRONT_LIGHT = false;

  // Per-pixel extras, measured on a Redmi Note 8T and switched off there: the
  // relief does not read at block size on a phone and the second specular lobe
  // doubles the lighting maths. Flipping these back on also restores the
  // matching texture downloads.
  public static readonly USE_NORMAL_MAPS = true;
  public static readonly USE_CLEARCOAT = false;
  public static readonly FPS_OVERLAY_WINDOW_MS = 60_000;
  public static readonly FPS_OVERLAY_SAMPLE_INTERVAL_MS = 100;

  private static _env: RuntimeEnvironmentKey | null = null;

  // Resolved once: an optional localStorage override (applied on next launch,
  // handy for on-device testing) wins over the branch baked in at build time.
  public static get ENV(): RuntimeEnvironmentKey
  {
    if ( GlobalSettings._env === null )
    {
      GlobalSettings._env = getActiveRuntimeEnvironmentKey();
    }
    return GlobalSettings._env;
  }

  public static get IS_PROD(): boolean
  {
    return GlobalSettings.ENV === RuntimeEnvironmentKey.PROD;
  }

  public static get API_BASE_URL(): string
  {
    // Dev-only escape hatch: point the client at a locally running server
    // (localStorage.setItem('api_base_override', 'http://localhost:3000')).
    if ( GlobalSettings.ENV !== RuntimeEnvironmentKey.PROD )
    {
      try
      {
        const override = localStorage.getItem( "api_base_override" );
        if ( override )
          return override.replace( /\/+$/, "" );
      }
      catch { /* storage unavailable — fall through */ }
    }

    return getServerRoutingOverride()?.api_url ?? GlobalSettings.HOME_API_BASE_URL;
  }

  public static get WEBSOCKET_URL(): string
  {
    if ( GlobalSettings.ENV !== RuntimeEnvironmentKey.PROD )
    {
      try
      {
        const override = localStorage.getItem( "websocket_url_override" );
        const normalized = override?.trim();
        if ( normalized ) return normalized;
      }
      catch {}
    }

    return getServerRoutingOverride()?.ws_url
      ?? WEBSOCKET_URLS[GlobalSettings.ENV]
      ?? WEBSOCKET_URLS[RuntimeEnvironmentKey.MAIN];
  }

  public static get ATHENA_ANALYTICS_BASE_URL(): string
  {
    try
    {
      return localStorage.getItem( "athena_analytics_endpoint_url" ) ?? "";
    }
    catch
    {
      return "";
    }
  }
}

export const DebugSettings = {
  DEBUG_CONSOLE_TAP_CODE: [ 1, 2, 3, 4, 1, 2, 3, 4 ],
  DEBUG_CONSOLE_TAP_CODE_WINDOW_MS: 3000,
  SHOW_DEBUG_CONSOLE_ENTRY_BUTTON: !GlobalSettings.IS_PROD,
};
